/* ═══════════════════════════════════════════════════════════════
   PiARIS — Delta Recon Showcase
   Amber → Red gradient. Venn diagram (A/B/Δ), side-by-side scan
   comparison, resolution stats, feature cards.
   © 2026 Pi Consultants · pi-consulting.us
   ═══════════════════════════════════════════════════════════════ */
import React from "react";
import { PRODUCT_GRADIENTS } from "./gradients";
import { ProductLockup } from "./ProductLockup";

var E = React.createElement;
var G = PRODUCT_GRADIENTS.deltarecon;

/** Venn diagram SVG: Scan A ∩ Scan B with Δ overlap label */
function VennSvg() {
  return E("svg", {
    width: 160, height: 130, viewBox: "0 0 160 130",
    "aria-hidden": "true",
    style: { filter: "drop-shadow(0 4px 16px rgba(255,255,255,0.2))" }
  },
    E("defs", null,
      E("radialGradient", { id: "dr-circle-a", cx: "50%", cy: "50%", r: "50%" },
        E("stop", { offset: "0%",  stopColor: "rgba(251,191,36,0.7)" }),
        E("stop", { offset: "100%", stopColor: "rgba(251,191,36,0.2)" })
      ),
      E("radialGradient", { id: "dr-circle-b", cx: "50%", cy: "50%", r: "50%" },
        E("stop", { offset: "0%",  stopColor: "rgba(248,113,113,0.7)" }),
        E("stop", { offset: "100%", stopColor: "rgba(248,113,113,0.2)" })
      )
    ),
    /* Circle A */
    E("circle", {
      cx: 60, cy: 65, r: 42,
      fill: "url(#dr-circle-a)",
      stroke: "rgba(251,191,36,0.9)",
      strokeWidth: 2
    }),
    /* Circle B */
    E("circle", {
      cx: 100, cy: 65, r: 42,
      fill: "url(#dr-circle-b)",
      stroke: "rgba(248,113,113,0.9)",
      strokeWidth: 2
    }),
    /* Labels */
    E("text", {
      x: 38, y: 38, fill: "#fff", fontSize: 14, fontWeight: 800,
      fontFamily: "Inter, system-ui, sans-serif"
    }, "A"),
    E("text", {
      x: 116, y: 38, fill: "#fff", fontSize: 14, fontWeight: 800,
      fontFamily: "Inter, system-ui, sans-serif"
    }, "B"),
    E("text", {
      x: 73, y: 72, fill: "#fff", fontSize: 22, fontWeight: 900,
      fontFamily: "Inter, system-ui, sans-serif"
    }, "\u0394")
  );
}

/** Side-by-side A vs B comparison */
function ABCompare() {
  return E("figure", {
    "aria-label": "Scan A vs Scan B comparison",
    style: { margin: 0 }
  },
    E("figcaption", {
      style: {
        fontSize: 10,
        fontWeight: 700,
        color: "rgba(255,255,255,0.8)",
        textTransform: "uppercase",
        letterSpacing: ".06em",
        marginBottom: 6
      }
    }, "Compliance Trend"),
    E("div", {
      style: {
        background: "rgba(15,23,42,0.55)",
        border: "1px solid rgba(255,255,255,0.18)",
        borderRadius: 12,
        padding: "14px 16px",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)"
      }
    },
      E("div", {
        style: {
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          gap: 12,
          alignItems: "center"
        },
        className: "dr-ab-row"
      },
        /* Scan A */
        E("div", { style: { textAlign: "center" } },
          E("div", { style: { fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 } }, "Scan A \u00B7 Mar 1"),
          E("div", { style: { fontSize: 28, fontWeight: 900, color: "#FBBF24", lineHeight: 1 } }, "58%"),
          E("div", { style: { fontSize: 9, color: "rgba(255,255,255,0.6)", marginTop: 2 } }, "Partial")
        ),
        /* Arrow */
        E("div", {
          "aria-hidden": "true",
          className: "dr-arrow",
          style: {
            fontSize: 22,
            color: "#6EE7B7",
            fontWeight: 900
          }
        }, "\u2192"),
        /* Scan B */
        E("div", { style: { textAlign: "center" } },
          E("div", { style: { fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 } }, "Scan B \u00B7 Apr 5"),
          E("div", { style: { fontSize: 28, fontWeight: 900, color: "#6EE7B7", lineHeight: 1 } }, "72%"),
          E("div", { style: { fontSize: 9, color: "rgba(255,255,255,0.6)", marginTop: 2 } }, "Substantial")
        )
      ),
      /* Delta */
      E("div", {
        style: {
          marginTop: 10,
          textAlign: "center",
          padding: "6px 10px",
          background: "rgba(110,231,183,0.18)",
          border: "1px solid rgba(110,231,183,0.4)",
          borderRadius: 8,
          fontSize: 11,
          fontWeight: 800,
          color: "#A7F3D0"
        }
      }, "\u25B2 +14% improvement"),
      /* Resolution stats */
      E("dl", {
        style: {
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 8,
          marginTop: 12,
          marginBottom: 0
        }
      },
        [
          { k: "Resolved",   v: "639", c: "#6EE7B7" },
          { k: "Persistent", v: "987", c: "#FBBF24" },
          { k: "Regressed",  v: "216", c: "#FCA5A5" }
        ].map(function(s, i) {
          return E("div", {
            key: i,
            style: {
              background: "rgba(255,255,255,0.06)",
              borderRadius: 6,
              padding: "6px 4px",
              textAlign: "center"
            }
          },
            E("dt", { style: { fontSize: 8, fontWeight: 700, color: "rgba(255,255,255,0.6)", textTransform: "uppercase" } }, s.k),
            E("dd", { style: { fontSize: 14, fontWeight: 900, color: s.c, margin: "2px 0 0", fontFeatureSettings: '"tnum"' } }, s.v)
          );
        })
      )
    )
  );
}

function DeltaReconShowcase(p) {
  var ctaLabel = p.ctaLabel || "Open Delta Recon";
  return E("section", {
    role: "region",
    "aria-labelledby": "dr-showcase-title",
    style: {
      borderRadius: 18,
      overflow: "hidden",
      border: "2px solid " + G.border + "30",
      background: G.hero,
      color: "#FFFFFF",
      position: "relative",
      boxShadow: "0 10px 40px " + G.glow
    }
  },
    /* Header */
    E("div", {
      style: {
        position: "relative",
        zIndex: 1,
        padding: "28px 28px 20px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 20,
        alignItems: "center"
      },
      className: "dr-header"
    },
      E("div", null,
        E("div", {
          style: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10
          }
        },
          E(ProductLockup, { size: "small", white: true }),
          E("span", { "aria-hidden": "true", style: { color: "rgba(255,255,255,0.4)" } }, "/"),
          E("span", {
            style: {
              fontSize: 12,
              fontWeight: 700,
              color: "rgba(255,255,255,0.85)",
              textTransform: "uppercase",
              letterSpacing: ".08em"
            }
          }, "Delta Recon")
        ),
        E("h2", {
          id: "dr-showcase-title",
          style: {
            fontSize: 26,
            fontWeight: 900,
            margin: 0,
            lineHeight: 1.2,
            letterSpacing: "-0.4px"
          }
        }, "Scan-to-Scan Regression Tracking"),
        E("p", {
          style: {
            fontSize: 13,
            color: "rgba(255,255,255,0.82)",
            margin: "8px 0 0",
            lineHeight: 1.55,
            maxWidth: 480
          }
        }, "Compare any two scans page-by-page. See what was resolved, what persists, and what regressed. Generate DOCX progress reports for stakeholders.")
      ),
      E("div", { style: { flexShrink: 0 } }, E(VennSvg, null))
    ),
    /* Body */
    E("div", {
      style: {
        position: "relative",
        zIndex: 1,
        padding: "0 28px 28px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 16
      },
      className: "dr-body"
    },
      /* Feature cards */
      E("ul", {
        style: {
          display: "flex",
          flexDirection: "column",
          gap: 8,
          listStyle: "none",
          padding: 0,
          margin: 0,
          gridColumn: "1 / 2"
        }
      },
        [
          { i: "\uD83D\uDD0D", t: "Page-Level Diff", d: "Every page mapped: better, worse, same, added, removed" },
          { i: "\uD83D\uDCC8", t: "Trend Tracking",  d: "Score trajectory across multiple scans over time" },
          { i: "\uD83D\uDCC4", t: "DOCX Reports",   d: "Exportable progress reports for compliance officers" }
        ].map(function(f, i) {
          return E("li", {
            key: i,
            style: {
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 10,
              padding: "10px 12px"
            }
          },
            E("span", {
              "aria-hidden": "true",
              style: { fontSize: 18, lineHeight: 1, flexShrink: 0, marginTop: 2 }
            }, f.i),
            E("div", null,
              E("div", { style: { fontSize: 12, fontWeight: 800, color: "#fff", marginBottom: 2 } }, f.t),
              E("div", { style: { fontSize: 10, color: "rgba(255,255,255,0.75)", lineHeight: 1.4 } }, f.d)
            )
          );
        })
      ),
      E("div", { style: { gridColumn: "2 / 3" } }, E(ABCompare, null)),
      /* CTA */
      E("div", {
        style: {
          gridColumn: "1 / -1",
          marginTop: 4,
          display: "flex",
          gap: 10,
          flexWrap: "wrap"
        }
      },
        p.onCta ? E("button", {
          onClick: p.onCta,
          style: {
            minHeight: 44,
            padding: "10px 22px",
            borderRadius: 10,
            border: "none",
            background: "#FFFFFF",
            color: G.pri,
            fontSize: 13,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)"
          }
        }, "\u0394 " + ctaLabel) : null
      )
    )
  );
}

export { DeltaReconShowcase };
